import React, { useEffect, forwardRef, useImperativeHandle } from "react";
import { flushSync } from "react-dom";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Typography,
  Popover,
  IconButton,
  Drawer,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonIcon from "@mui/icons-material/Person";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../authContext/authContext";
import { usePortal } from "../authContext/portalContext";
import SettingsDialog from "./SettingsDialog";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { useTranslation } from "../i18n/LanguageContext";

const SIDEBAR_WIDTH = 260;

function SidebarPanelIcon({ size = 20, color = "currentColor" }) {
  return (
    <Box
      component="svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      sx={{ display: "block", color }}
    >
      <rect
        x="3.25"
        y="3.25"
        width="17.5"
        height="17.5"
        rx="3"
        stroke="currentColor"
        strokeWidth="1.75"
      />
      <path d="M9 3.5V20.5" stroke="currentColor" strokeWidth="1.75" />
    </Box>
  );
}

function NavIcon({ src }) {
  return (
    <Box
      component="span"
      aria-hidden
      sx={{
        width: 18,
        height: 18,
        display: "inline-block",
        flexShrink: 0,
        backgroundColor: "#000000",
        mask: `url(${src}) center / contain no-repeat`,
        WebkitMask: `url(${src}) center / contain no-repeat`,
      }}
    />
  );
}

const Sidebar = forwardRef(function Sidebar(props, ref) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, isAdmin, logout } = useAuth();
  const { activePortal, availablePortals, switchPortal } = usePortal();
  const [accountAnchorEl, setAccountAnchorEl] = React.useState(null);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [pinned, setPinned] = React.useState(true);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const accountOpen = Boolean(accountAnchorEl);

  useImperativeHandle(ref, () => ({
    openMobile: () => setMobileOpen(true),
  }));

  // Everyone with an active portal can open Settings; the Manage group
  // inside SettingsDialog is further restricted to true admins only.
  const canOpenSettings = Boolean(activePortal);

  const email = user?.email || "";
  const displayName = user?.name || (email ? email.split("@")[0] : "Account");
  const roleLabel =
    activePortal === "admin"
      ? isAdmin
        ? t("sidebar.roleLabels.admin")
        : t("sidebar.roleLabels.operator")
      : activePortal === "therapist"
      ? t("sidebar.roleLabels.therapist")
      : activePortal === "patient"
      ? t("sidebar.roleLabels.patient")
      : "";
  const initials = (displayName || email || "A").charAt(0).toUpperCase();

  // Other portals this account can switch into (in-app), besides the active one
  const otherPortals = availablePortals.filter((p) => p !== activePortal);

  const handleSwitchPortal = (portal) => {
    // flushSync forces the portal-switch state update to commit before we
    // navigate, so /dashboard's first render already sees the new
    // activePortal — without it, navigate() can land before React commits
    // the switch, so the old portal's dashboard briefly shows and a second
    // click is needed to actually see the new one.
    flushSync(() => {
      switchPortal(portal);
    });
    navigate("/dashboard");
    setMobileOpen(false);
  };

  // ⌘+, (Mac) / Ctrl+, (Windows/Linux) opens Settings
  useEffect(() => {
    if (!canOpenSettings) return undefined;

    const onKeyDown = (e) => {
      if (e.key !== "," || !(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey) return;
      // Ignore when typing in inputs/textareas/contenteditable
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable) return;

      e.preventDefault();
      setAccountAnchorEl(null);
      setSettingsOpen(true);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [canOpenSettings]);

  const handleLogout = () => {
    setAccountAnchorEl(null);
    logout();
    navigate("/login", { replace: true });
  };

  const handleCollapse = () => {
    setPinned(false);
    setMobileOpen(false);
    setAccountAnchorEl(null);
  };

  const handlePin = () => {
    setPinned(true);
  };

  const adminItems = [
    { label: "Dashboard", icon: "/icons/ic_dashboard.svg", path: "/dashboard" },
    { label: "Orders", icon: "/icons/ic_orders.svg", path: "/orders" },
    { label: "Analysis", icon: "/icons/ic_analysis.svg", path: "/analysis" },
    ...(isAdmin ? [{ label: "Review", icon: "/icons/ic_review.svg", path: "/review" }] : []),
    ...(isAdmin
      ? [
          { label: "Therapists", icon: "/icons/ic_therapist.svg", path: "/therapist" },
          { label: "Operators", icon: "/icons/ic_operators.svg", path: "/operators" },
          { label: "Portal Users", icon: "/icons/ic_portal_users.svg", path: "/portal-users" },
          { label: "Usage", icon: "/icons/ic_usage.svg", path: "/usage" },
        ]
      : []),
  ];

  const patientItems = [
    { label: t("sidebar.nav.dashboard"), icon: "/icons/ic_dashboard.svg", path: "/dashboard" },
    { label: t("sidebar.nav.myOrders"), icon: "/icons/ic_orders.svg", path: "/patient/orders" },
    { label: t("sidebar.nav.myReports"), icon: "/icons/ic_completed.svg", path: "/patient/reports" },
  ];

  const therapistItems = [
    { label: t("sidebar.nav.dashboard"), icon: "/icons/ic_dashboard.svg", path: "/dashboard" },
    { label: t("sidebar.nav.myPatients"), icon: "/icons/ic_therapist.svg", path: "/therapist/patients" },
    { label: t("sidebar.nav.reports"), icon: "/icons/ic_completed.svg", path: "/therapist/reports" },
  ];

  const items =
    activePortal === "admin"
      ? adminItems
      : activePortal === "patient"
      ? patientItems
      : activePortal === "therapist"
      ? therapistItems
      : [];

  const panelSx = {
    width: SIDEBAR_WIDTH,
    minWidth: SIDEBAR_WIDTH,
    height: "100vh",
    bgcolor: "#EBEBEB",
    borderRight: "1px solid #e5e7eb",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  };

  const sidebarPanel = (
    <Box sx={panelSx}>
      {/* Top: Logo, toggle & Nav */}
      <Box sx={{ minHeight: 0, display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            px: 1.5,
            py: 1.5,
          }}
        >
          <img
            src="/Sibolab_Logo_darkblue_rgb.png"
            alt="Logo"
            style={{ height: "24px", width: "auto" }}
          />
          <IconButton
            size="small"
            onClick={handleCollapse}
            aria-label="Hide sidebar"
            sx={{
              color: "#6b7280",
              borderRadius: 1.5,
              "&:hover": { bgcolor: "#e0e0e0", color: "#1a1a1a" },
            }}
          >
            <SidebarPanelIcon size={20} />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 1 }} />

        <List sx={{ p: 1, overflowY: "auto", flex: 1, minHeight: 0 }}>
          {items.map((item) => {
            const isSelected = location.pathname === item.path;
            const isAnalysisSelected =
              item.path === "/analysis" && location.pathname.startsWith("/analysis");
            const isReviewSelected =
              item.path === "/review" && location.pathname.startsWith("/review");
            const isTherapistPatientsSelected =
              item.path === "/therapist/patients" && location.pathname.startsWith("/therapist/patients");
            const isUsageSelected = item.path === "/usage" && location.pathname.startsWith("/usage");
            const active =
              isSelected || isAnalysisSelected || isReviewSelected || isTherapistPatientsSelected || isUsageSelected;

            return (
              <ListItemButton
                key={item.label}
                onClick={() => {
                  navigate(item.path);
                  setMobileOpen(false);
                }}
                sx={{
                  mb: 0.25,
                  mx: 0.75,
                  width: "auto",
                  borderRadius: 1.5,
                  px: 1.25,
                  py: 0.625,
                  bgcolor: active ? "#FFFFFF" : "transparent",
                  color: "#000000",
                  display: "flex",
                  alignItems: "center",
                  "&:hover": {
                    backgroundColor: active ? "#FFFFFF" : "#e0e0e0",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: 1.25,
                    justifyContent: "center",
                  }}
                >
                  <NavIcon src={item.icon} active={active} />
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: "0.8125rem",
                    fontWeight: active ? 500 : 400,
                    noWrap: true,
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Box>

      {/* Bottom: Portals + Account */}
      <Box sx={{ p: 1, pt: 0, flexShrink: 0 }}>
        <Divider sx={{ mb: 1 }} />

        {otherPortals.length > 0 && (
        <>
        <Box
          sx={{
            mb: 1,
            p: 0.5,
            borderRadius: 2,
            bgcolor: "#163666",
            boxShadow: "0 1px 2px rgba(16,24,40,0.08)",
          }}
        >
          {otherPortals.map((portal) => (
            <Box
              key={portal}
              component="button"
              type="button"
              onClick={() => handleSwitchPortal(portal)}
              sx={{
                all: "unset",
                boxSizing: "border-box",
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: "100%",
                px: 1.25,
                py: 0.875,
                borderRadius: 1.5,
                cursor: "pointer",
                color: "#FFFFFF",
                transition: "background-color 0.12s",
                "&:hover": { backgroundColor: "#112B51" },
                "&:hover .portal-arrow": { opacity: 1 },
              }}
            >
              <Typography sx={{ flex: 1, fontSize: "0.8125rem", fontWeight: 500 }}>
                {t(`sidebar.portalLabels.${portal}`)}
              </Typography>
              <NorthEastIcon
                className="portal-arrow"
                sx={{
                  fontSize: 14,
                  color: "rgba(255,255,255,0.55)",
                  opacity: 0,
                  transition: "opacity 0.12s",
                }}
              />
            </Box>
          ))}
        </Box>
        <Divider sx={{ mb: 1 }} />
        </>
        )}

        <Box
          onClick={(e) => setAccountAnchorEl(accountOpen ? null : e.currentTarget)}
          aria-haspopup="menu"
          aria-expanded={accountOpen}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderRadius: 2,
            px: 1,
            py: 0.75,
            cursor: "pointer",
            bgcolor: accountOpen ? "#FFFFFF" : "transparent",
            boxShadow: accountOpen ? "0 1px 2px rgba(16,24,40,0.06)" : "none",
            transition: "background-color 0.15s, box-shadow 0.15s",
            "&:hover": { backgroundColor: "#FFFFFF" },
          }}
        >
          <Avatar
            sx={{
              width: 28,
              height: 28,
              bgcolor: "#163666",
              fontSize: "0.75rem",
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {email || displayName ? initials : <PersonIcon sx={{ fontSize: 16 }} />}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              noWrap
              sx={{ fontSize: "0.8125rem", fontWeight: 500, color: "#1a1a1a", lineHeight: 1.25 }}
            >
              {displayName}
            </Typography>
            <Typography
              noWrap
              sx={{ fontSize: "0.6875rem", color: "#6b7280", lineHeight: 1.3 }}
            >
              {roleLabel}
            </Typography>
          </Box>
          <ExpandMoreIcon
            sx={{
              fontSize: 18,
              color: "#9ca3af",
              transform: accountOpen ? "rotate(180deg)" : "none",
              transition: "transform 0.15s",
            }}
          />
        </Box>

        <Popover
          open={accountOpen}
          anchorEl={accountAnchorEl}
          onClose={() => setAccountAnchorEl(null)}
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          transformOrigin={{ vertical: "bottom", horizontal: "left" }}
          slotProps={{
            paper: {
              elevation: 0,
              sx: {
                width: SIDEBAR_WIDTH - 16,
                mt: -0.5,
                borderRadius: 2.5,
                border: "1px solid #e5e7eb",
                boxShadow:
                  "0 12px 32px rgba(16,24,40,0.12), 0 2px 6px rgba(16,24,40,0.06)",
                overflow: "hidden",
                bgcolor: "#FFFFFF",
              },
            },
          }}
        >
          <Box sx={{ px: 1.75, pt: 1.5, pb: 1 }}>
            <Typography
              noWrap
              sx={{ fontSize: "0.75rem", color: "#6b7280", lineHeight: 1.4 }}
            >
              {email || t("sidebar.account.signedIn")}
            </Typography>
          </Box>

          <Divider sx={{ borderColor: "#F3F4F6" }} />

          <Box sx={{ p: 0.75 }}>
            {canOpenSettings && (
              <Box
                component="button"
                type="button"
                onClick={() => {
                  setAccountAnchorEl(null);
                  setSettingsOpen(true);
                }}
                sx={{
                  all: "unset",
                  boxSizing: "border-box",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  width: "100%",
                  px: 1.25,
                  py: 1,
                  borderRadius: 1.5,
                  cursor: "pointer",
                  color: "#374151",
                  transition: "background-color 0.12s",
                  "&:hover": { backgroundColor: "#F3F4F6" },
                }}
              >
                <SettingsOutlinedIcon sx={{ fontSize: 18, color: "#6b7280" }} />
                <Typography sx={{ fontSize: "0.8125rem", fontWeight: 500, flex: 1 }}>
                  {t("sidebar.account.settings")}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.6875rem",
                    color: "#9ca3af",
                    fontFamily:
                      "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                    letterSpacing: "0.02em",
                  }}
                >
                  {typeof navigator !== "undefined" &&
                  /Mac|iPhone|iPad/.test(navigator.platform)
                    ? "⌘,"
                    : "Ctrl+,"}
                </Typography>
              </Box>
            )}
            <Box
              component="button"
              type="button"
              onClick={handleLogout}
              sx={{
                all: "unset",
                boxSizing: "border-box",
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                width: "100%",
                px: 1.25,
                py: 1,
                borderRadius: 1.5,
                cursor: "pointer",
                color: "#DC2626",
                transition: "background-color 0.12s",
                "&:hover": { backgroundColor: "#FEF2F2" },
              }}
            >
              <Box
                component="span"
                aria-hidden
                sx={{
                  width: 18,
                  height: 18,
                  display: "inline-block",
                  flexShrink: 0,
                  backgroundColor: "currentColor",
                  mask: "url(/icons/ic_logout.svg) center / contain no-repeat",
                  WebkitMask: "url(/icons/ic_logout.svg) center / contain no-repeat",
                }}
              />
              <Typography sx={{ fontSize: "0.8125rem", fontWeight: 500 }}>
                {t("sidebar.account.logout")}
              </Typography>
            </Box>
          </Box>
        </Popover>
      </Box>
    </Box>
  );

  return (
    <>
      {/* In-flow spacer when pinned so main content shifts correctly (desktop only) */}
      {pinned && (
        <Box
          sx={{
            display: { xs: "none", md: "block" },
            width: SIDEBAR_WIDTH,
            minWidth: SIDEBAR_WIDTH,
            flexShrink: 0,
            height: "100vh",
          }}
        >
          {sidebarPanel}
        </Box>
      )}

      {/* Collapsed: floating toggle to show sidebar again (desktop only) */}
      {!pinned && (
        <Box
          sx={{
            display: { xs: "none", md: "block" },
            position: "fixed",
            left: 0,
            top: 0,
            zIndex: 1300,
            pt: 1.5,
            pl: 1.5,
          }}
        >
          <IconButton
            size="small"
            onClick={handlePin}
            aria-label="Show sidebar"
            sx={{
              color: "#6b7280",
              bgcolor: "#EBEBEB",
              border: "1px solid #e5e7eb",
              borderRadius: 1.5,
              boxShadow: "0 1px 3px rgba(16,24,40,0.08)",
              "&:hover": { bgcolor: "#e0e0e0", color: "#1a1a1a" },
            }}
          >
            <SidebarPanelIcon size={20} />
          </IconButton>
        </Box>
      )}

      {/* Mobile: overlay drawer, triggered by the hamburger icon in MobileTopBar */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { width: SIDEBAR_WIDTH, boxSizing: "border-box" },
        }}
      >
        {sidebarPanel}
      </Drawer>

      {canOpenSettings && (
        <SettingsDialog
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
        />
      )}
    </>
  );
});

export default Sidebar;
